import React, { useEffect, useState } from "react";
import { Button, Skeleton, CircularProgress } from "@mui/material";

import UserSearch from "../Search/UserSearch";
import { UserType } from "../../types/user";

import FastAPIClient from "../../client";
import fast_api_config from "../../config";

import { UserAccessManagerProps } from "../../types/user_access_manager";

// Instantiate the FastAPI client
const client = new FastAPIClient(fast_api_config);

const UserAccessManager: React.FC<UserAccessManagerProps> = ({
    onClose,
    clientId,
}) => {
    const [usersWithAccess, setUsersWithAccess] = useState<UserType[]>([]);
    const [usersList, setUsersList] = useState<UserType[]>([]);
    const [initialUsersWithAccess, setInitialUsersWithAccess] = useState<
        UserType[]
    >([]); // Store initial state
    const [loading, setLoading] = useState<boolean>(true); // Loading state
    const [isSaving, setIsSaving] = useState<boolean>(false); // Saving state

    // Handle adding a user
    const handleAddUser = (selectedUser: UserType) => {
        if (
            !usersWithAccess.some(
                (user) => user.user_id === selectedUser.user_id
            )
        ) {
            setUsersWithAccess([...usersWithAccess, selectedUser]);
        }
    };

    // Handle removing a user
    const handleRemoveUser = (userId: string) => {
        setUsersWithAccess(
            usersWithAccess.filter((user) => user.user_id !== userId)
        );
    };

    // Handle selection from UserSearch, where we receive only a user_id
    const handleSelect = (userId: string) => {
        const selectedUser = usersList.find((user) => user.user_id === userId);
        if (selectedUser) {
            handleAddUser(selectedUser);
        }
    };

    const getUsers = async () => {
        try {
            // Fetch users from the API
            const userData = await client.getUsers();

            // Fetch client users with user_id and client_id
            const clientUsers = await client.fetchClientUsers(clientId);

            // Filter only active users
            const activeUsers: UserType[] = userData.results.filter(
                (user: UserType) => user.is_active
            );

            // Set the users list (all active users)
            setUsersList(activeUsers);

            // Match clientUsers with active user details
            const usersWithDetails = clientUsers
                .map((clientUser) => {
                    const userDetails = activeUsers.find(
                        (user) => user.user_id === clientUser.user_id
                    );
                    return userDetails
                        ? { ...clientUser, ...userDetails }
                        : null;
                })
                .filter((user) => user !== null);

            // Set initial users with access
            setUsersWithAccess(usersWithDetails as UserType[]);
            setInitialUsersWithAccess(usersWithDetails as UserType[]); // Store the initial state
        } catch (error) {
            console.error("Error fetching users:", error);
        } finally {
            setLoading(false); // Set loading to false after data is fetched
        }
    };

    const handleSave = async () => {
        // Check if the usersWithAccess have changed
        const isChanged =
            JSON.stringify(usersWithAccess) !==
            JSON.stringify(initialUsersWithAccess);

        if (isChanged) {
            setIsSaving(true); // Start saving state

            try {
                // Call API to update client users with the new list
                await client.updateClientUsers(clientId, usersWithAccess);
                // After successful update, close the modal
                onClose();
            } catch (error) {
                console.error("Error updating client users:", error);
                // Do not close the modal if there is an error
            } finally {
                setIsSaving(false); // Stop saving state
            }
        } else {
            // If no changes, simply close the modal
            onClose();
        }
    };

    useEffect(() => {
        getUsers();
    }, []);

    return (
        // Full-screen backdrop to center the modal
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            {/* Modal */}
            <div className="w-full md:w-1/3 bg-white border border-gray-200 p-6 rounded-xl shadow-lg relative">
                {/* Close Button */}
                <button
                    className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
                    onClick={onClose}
                >
                    ✕
                </button>

                <h3 className="text-xl font-bold mb-4">Partager</h3>

                {/* User Search Component */}
                <UserSearch
                    placeholder="Ajouter des collègues"
                    sharedCalUsersList={null}
                    usersList={usersList}
                    handleSelect={handleSelect}
                />

                {/* Users with access */}
                <div className="text-lg pt-4 font-semibold">Partagé avec</div>
                <ul className="list-disc pl-6 mt-2">
                    {loading ? (
                        // Skeleton loading state
                        <>
                            <Skeleton variant="text" width="80%" height={30} />
                            <Skeleton variant="text" width="60%" height={30} />
                            <Skeleton variant="text" width="70%" height={30} />
                        </>
                    ) : usersWithAccess.length > 0 ? (
                        usersWithAccess.map((user) => (
                            <li
                                key={user.user_id}
                                className="flex items-center justify-between py-1"
                            >
                                <div className="flex flex-col text-left">
                                    <span>
                                        {user.first_name} {user.last_name}
                                    </span>
                                    <span className="text-gray-400">
                                        {user.email}
                                    </span>
                                </div>
                                <button
                                    className="text-red-600 hover:text-red-800"
                                    onClick={() =>
                                        handleRemoveUser(user.user_id)
                                    }
                                >
                                    ✕
                                </button>
                            </li>
                        ))
                    ) : (
                        <li className="text-gray-500">No users with access.</li>
                    )}
                </ul>

                <div className="flex w-full flex-row mt-4">
                    <div className="flex w-full justify-end">
                        <Button
                            onClick={handleSave}
                            sx={{
                                borderRadius: "999px",
                                backgroundColor: "#C3FF36",
                                color: "black",
                                textTransform: "none",
                                "&:disabled": {
                                    backgroundColor: "lightgray",
                                    color: "darkgray",
                                },
                            }}
                            disabled={isSaving} // Disable the button while saving
                        >
                            {isSaving ? (
                                <CircularProgress
                                    size={16}
                                    sx={{ color: "black" }}
                                />
                            ) : (
                                "Enregistrer"
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserAccessManager;
